import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectComponent, SelectOption } from '../../ui/select/select.component';

@Component({
  selector: 'app-device-selector',
  standalone: true,
  imports: [SelectComponent, FormsModule, MatIconModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (devices().length > 0) {
      <div class="device-selector-field text-left flex items-center gap-2 w-full min-w-0">
        <mat-icon class="flex items-center justify-center text-gray-500 shrink-0">{{ icon() }}</mat-icon>
        <ui-select
          class="flex-grow w-full min-w-0"
          [options]="selectOptions()"
          [ngModel]="selectedId()"
          (valueChange)="selectionChange.emit($any($event))"
          [disabled]="isDisabled()"
          [compact]="true"
        ></ui-select>
      </div>
    } @else {
      <div class="no-devices-found">
        <mat-icon>{{ icon() }}</mat-icon>
        <span>{{ 'DeviceSelector.NoDeviceFound' | translate: { deviceType: translatedDeviceType() } }}</span>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
      .device-selector-field {
        width: 100%;
      }
      ui-select {
        flex-grow: 1;
        width: 100%;
        min-width: 0;
        :host::ng-deep .skz-select-trigger.compact {
          display: flex;
          width: 100%;
        }
      }
      .no-devices-found {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 0;
        font-style: italic;
        opacity: 0.7;
        font-size: 0.8125rem;
      }
    `,
  ],
})
export class DeviceSelectorComponent {
  kind = input.required<'audioinput' | 'videoinput' | 'audiooutput'>();
  label = input.required<string>();
  devices = input.required<MediaDeviceInfo[]>();
  selectedId = input<string | null>(null);
  isDisabled = input<boolean>(false);
  selectionChange = output<string>();

  private translate = inject(TranslateService);

  icon = computed(() => {
    if (this.kind() === 'audioinput') return 'settings_voice';
    if (this.kind() === 'audiooutput') return 'volume_up';
    return 'videocam';
  });

  translatedDeviceType = computed(() => {
    if (this.kind() === 'audioinput') return this.translate.instant('DeviceSelector.Microphone');
    if (this.kind() === 'audiooutput') return this.translate.instant('DeviceSelector.Speaker');
    return this.translate.instant('DeviceSelector.Camera');
  });

  selectOptions = computed<SelectOption[]>(() => {
    return this.devices().map((device, index) => ({
      value: device.deviceId,
      label: this.cleanLabel(device.label) || `${this.translate.instant('DeviceSelector.UnknownDevice')} ${index + 1}`,
    }));
  });

  cleanLabel(label: string): string {
    if (!label) return '';
    let clean = label.replace(/\s*\([^)]*\)/g, '');
    clean = clean.replace(/\bbuilt-in\b/gi, '');
    clean = clean.replace(/\bdevice\b/gi, '');
    return clean.replace(/\s+/g, ' ').trim();
  }
}
